import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/blocks";
import api from "@/lib/api";
import { eur } from "@/context/CartContext";

export function ProductCard({ p, i = 0, catName }) {
  return (
    <Reveal delay={(i % 4) * 0.06}>
      <Link to={`/boutique/${p.slug}`} className="group block hp-card overflow-hidden h-full" data-testid={`product-card-${p.slug}`}>
        <div className="relative aspect-square bg-white overflow-hidden">
          <img src={p.image} alt={p.name} className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          {p.featured && <span className="absolute left-3 top-3 bg-hp-yellow text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1"><Sparkles size={11} /> Best-seller</span>}
          {!p.available && <span className="absolute right-3 top-3 bg-black/80 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">Bientôt</span>}
        </div>
        <div className="p-3.5 sm:p-5">
          <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-hp-gold font-bold truncate">{catName || p.category}</p>
          <h3 className="font-display font-bold uppercase tracking-tight text-sm sm:text-lg mt-1 leading-tight group-hover:text-hp-yellow transition-colors">{p.name}</h3>
          <div className="flex items-center justify-between mt-3">
            <span className="font-display text-base sm:text-xl" data-testid={`product-price-${p.slug}`}>{p.available ? eur(p.price) : <span className="text-neutral-500 text-sm">Prix à venir</span>}</span>
            <span className="hidden sm:inline-flex text-xs uppercase tracking-wide text-neutral-400 group-hover:text-hp-yellow items-center gap-1"><ShoppingBag size={14} /> Voir</span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Shop() {
  const [data, setData] = useState({ products: [], categories: [] });
  const [cat, setCat] = useState("all");
  useEffect(() => { api.get("/shop/products").then(({ data }) => setData(data)); }, []);
  const list = useMemo(() => (cat === "all" ? data.products : data.products.filter((p) => p.category === cat)), [data, cat]);

  return (
    <div data-testid="shop-page">
      <Seo title="Boutique officielle | Horsepower Garage" description="Vêtements, casquettes, sweats et goodies officiels Horsepower Garage. Retrait en centre ou livraison à domicile."
        canonical="https://www.horsepowergarage.fr/boutique" />
      <PageHero eyebrow="Boutique officielle" title="Portez les couleurs Horsepower" subtitle="Textile brodé, casquettes, accessoires et goodies aux couleurs du réseau. Retrait gratuit dans votre centre ou livraison à domicile." image="/photos/centre-beziers-full.jpg" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex flex-wrap gap-2" data-testid="shop-filters">
          {[{ slug: "all", name: "Tout" }, ...data.categories].map((c) => (
            <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`shop-filter-${c.slug}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${cat === c.slug ? "bg-hp-yellow text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}>{c.name}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-10" data-testid="shop-grid">
          {list.map((p, i) => <ProductCard key={p.slug} p={p} i={i} catName={data.categories.find((c) => c.slug === p.category)?.name} />)}
        </div>
      </section>
    </div>
  );
}
